import * as i0 from '@angular/core';
import { Input, Directive, Pipe, Optional, Injectable, NgModule } from '@angular/core';
import { NavigationStart, NavigationEnd, NavigationCancel, NavigationError, Router } from '@angular/router';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { filter } from 'rxjs';
import * as i1 from '@yourcause/common/i18n';
import { isEqual } from 'lodash';
import * as i1$1 from '@yourcause/common/currency';

class HiddenDirective {
    get ycHidden() {
        return this._ycHidden;
    }
    set ycHidden(value) {
        this._ycHidden = value;
        this.setHidden();
    }
    constructor(elementRef) {
        this.elementRef = elementRef;
    }
    setHidden() {
        if (this.ycHidden) {
            this.elementRef.nativeElement.classList.add('hidden');
            this.elementRef.nativeElement.setAttribute('aria-hidden', 'true');
        }
        else {
            this.elementRef.nativeElement.classList.remove('hidden');
            this.elementRef.nativeElement.setAttribute('aria-hidden', 'false');
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: HiddenDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: HiddenDirective, isStandalone: false, selector: "[ycHidden]", inputs: { ycHidden: "ycHidden" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: HiddenDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ycHidden]',
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { ycHidden: [{
                type: Input
            }] } });

class NullishCoalescePipe {
    transform(val, fallback) {
        return val ?? fallback;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: NullishCoalescePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: NullishCoalescePipe, isStandalone: false, name: "ycNullishCoalesce" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: NullishCoalescePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'ycNullishCoalesce',
                    pure: true,
                    standalone: false
                }]
        }] });

// copied from https://github.com/TrilonIO/angular-application-insights/blob/master/src/app-insight.service.ts
// Since AI.SeverityLevel isn't working we can just use our own
var SeverityLevel;
(function (SeverityLevel) {
    SeverityLevel[SeverityLevel["Verbose"] = 0] = "Verbose";
    SeverityLevel[SeverityLevel["Information"] = 1] = "Information";
    SeverityLevel[SeverityLevel["Warning"] = 2] = "Warning";
    SeverityLevel[SeverityLevel["Error"] = 3] = "Error";
    SeverityLevel[SeverityLevel["Critical"] = 4] = "Critical";
})(SeverityLevel || (SeverityLevel = {}));
class AppInsightsConfig {
}
class AppInsightsService {
    get context() {
        return this.appInsights?.context;
    }
    constructor(_config, _injector) {
        this._injector = _injector;
        this.config = _config;
    }
    // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md#trackevent
    /**
     * Log a user action or other occurrence.
     *
     * @param   name    A string to identify this event in the portal.
     * @param   properties  map[string, string] - additional data used to filter events and metrics in the portal. Defaults to empty.
     * @param   measurements    map[string, number] - metrics associated with this event, displayed in Metrics Explorer on the portal. Defaults to empty.
     */
    trackEvent(eventName, eventProperties) {
        try {
            this.appInsights?.trackEvent({ name: eventName }, eventProperties);
        }
        catch (ex) {
            console.warn('Angular application insights Error [trackEvent]: ', ex);
        }
    }
    /**
     * Start timing an extended event. Call {@link stopTrackEvent} to log the event when it ends.
     *
     * @param   name    A string that identifies this event uniquely within the document.
     */
    startTrackEvent(name) {
        try {
            this.appInsights?.startTrackEvent(name);
        }
        catch (ex) {
            console.warn('Angular application insights Error [startTrackEvent]: ', ex);
        }
    }
    /**
     * Log an extended event that you started timing with {@link startTrackEvent}.
     *
     * @param   name    The string you used to identify this event in startTrackEvent.
     * @param   properties  map[string, string] - additional data used to filter events and metrics in the portal. Defaults to empty.
     * @param   measurements    map[string, number] - metrics associated with this event, displayed in Metrics Explorer on the portal. Defaults to empty.
     */
    stopTrackEvent(name, properties, measurements) {
        try {
            this.appInsights?.stopTrackEvent(name, properties, measurements);
        }
        catch (ex) {
            console.warn('Angular application insights Error [stopTrackEvent]: ', ex);
        }
    }
    // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md#trackpageview
    /**
     * Logs that a page or other item was viewed.
     *
     * @param   name  The string you used as the name in startTrackPage. Defaults to the document title.
     * @param   url   String - a relative or absolute URL that identifies the page or other item. Defaults to the window location.
     * @param   properties  map[string, string] - additional data used to filter pages and metrics in the portal. Defaults to empty.
     * @param   measurements    map[string, number] - metrics associated with this page, displayed in Metrics Explorer on the portal. Defaults to empty.
     * @param   duration number - the number of milliseconds it took to load the page. Defaults to undefined. If set to default value, page load time is calculated internally.
     */
    trackPageView(name, url, properties, measurements, duration) {
        try {
            this.appInsights?.trackPageView({
                name,
                uri: url,
                properties: {
                    ...properties,
                    duration
                }
            });
        }
        catch (ex) {
            console.warn('Angular application insights Error [trackPageView]: ', ex);
        }
    }
    // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md#starttrackpage
    /**
     * Starts timing how long the user views a page or other item. Call this when the page opens.
     * This method doesn't send any telemetry. Call {@link stopTrackTelemetry} to log the page when it closes.
     *
     * @param   name  A string that idenfities this item, unique within this HTML document. Defaults to the document title.
     */
    startTrackPage(name) {
        try {
            this.appInsights?.startTrackPage(name);
        }
        catch (ex) {
            console.warn('Angular application insights Error [startTrackPage]: ', ex);
        }
    }
    // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md#stoptrackpage
    /**
     * Logs how long a page or other item was visible, after {@link startTrackPage}. Call this when the page closes.
     *
     * @param   name  The string you used as the name in startTrackPage. Defaults to the document title.
     * @param   url   String - a relative or absolute URL that identifies the page or other item. Defaults to the window location.
     * @param   properties  map[string, string] - additional data used to filter pages and metrics in the portal. Defaults to empty.
     * @param   measurements    map[string, number] - metrics associated with this page, displayed in Metrics Explorer on the portal. Defaults to empty.
     */
    stopTrackPage(name, url, properties, measurements) {
        try {
            this.appInsights?.stopTrackPage(name, url, properties, measurements);
        }
        catch (ex) {
            console.warn('Angular application insights Error [stopTrackPage]: ', ex);
        }
    }
    // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md#trackmetric
    /**
     * Log a numeric value that is not associated with a specific event. Typically used to send regular reports of performance indicators.
     * To send a single measurement, use just the first two parameters. If you take measurements very frequently, you can reduce the
     * telemetry bandwidth by aggregating multiple measurements and sending the resulting average at intervals.
     *
     * @param   name    A string that identifies the metric.
     * @param   average Number representing either a single measurement, or the average of several measurements.
     * @param   sampleCount The number of measurements represented by the average. Defaults to 1.
     * @param   min The smallest measurement in the sample. Defaults to the average.
     * @param   max The largest measurement in the sample. Defaults to the average.
     */
    trackMetric(name, average, sampleCount, min, max, properties) {
        try {
            this.appInsights?.trackMetric({
                name,
                average,
                sampleCount,
                min,
                max,
                properties
            });
        }
        catch (ex) {
            console.warn('Angular application insights Error [trackTrace]: ', ex);
        }
    }
    // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md#trackexception
    /**
     * Log an exception you have caught.
     *
     * @param   exception   An Error from a catch clause, or the string error message.
     * @param   properties  map[string, string] - additional data used to filter events and metrics in the portal. Defaults to empty.
     * @param   measurements    map[string, number] - metrics associated with this event, displayed in Metrics Explorer on the portal. Defaults to empty.
     * @param   severityLevel   SeverityLevel | AI.SeverityLevel - severity level
     */
    trackException(exception, handledAt, properties, measurements) {
        try {
            this.appInsights?.trackException({
                exception,
                properties: {
                    ...properties,
                    ...measurements,
                    handledAt
                }
            });
        }
        catch (ex) {
            console.warn('Angular application insights Error [trackException]: ', ex);
        }
    }
    // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md#trackdependency
    /**
     * Log a dependency call (for instance: ajax)
     *
     * @param   id    unique id, this is used by the backend o correlate server requests. Use Util.newId() to generate a unique Id.
     * @param   method    represents request verb (GET, POST, etc.)
     * @param   absoluteUrl   absolute url used to make the dependency request
     * @param   pathName  the path part of the absolute url
     * @param   totalTime total request time
     * @param   success   indicates if the request was sessessful
     * @param   resultCode    response code returned by the dependency request
     * @param   properties    map[string, string] - additional data used to filter events and metrics in the portal. Defaults to empty.
     * @param   measurements  map[string, number] - metrics associated with this event, displayed in Metrics Explorer on the portal. Defaults to empty.
     */
    trackDependency(id, method, absoluteUrl, pathName, totalTime, success, responseCode, properties, measurements) {
        try {
            this.appInsights?.trackDependencyData({
                id,
                duration: totalTime,
                success,
                responseCode,
                name: pathName,
                properties: {
                    absoluteUrl,
                    method,
                    ...properties,
                    ...measurements
                }
            });
        }
        catch (ex) {
            console.warn('Angular application insights Error [trackDependency]: ', ex);
        }
    }
    // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md#flush
    // flush()
    // Immediately send all queued telemetry. Synchronous.
    // * You don't usually have to use this, as it happens automatically on window closing.
    flush() {
        try {
            this.appInsights?.flush();
        }
        catch (ex) {
            console.warn('Angular application insights Error [flush]: ', ex);
        }
    }
    // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md#setauthenticatedusercontext
    /**
     * Sets the authenticated user id and the account id.
     * User auth id and account id should be of type string. They should not contain commas, semi-colons, equal signs, spaces, or vertical-bars.
     *
     * By default the method will only set the authUserID and accountId for all events in this page view. To add them to all events within
     * the whole session, you should either call this method on every page view or set `storeInCookie = true`.
     *
     * @param authenticatedUserId {string} - The authenticated user id. A unique and persistent string that represents each authenticated user in the service.
     * @param accountId {string} - An optional string to represent the account associated with the authenticated user.
     * @param storeInCookie {boolean} - AuthenticateUserID will be stored in a cookie and added to all events within this session.
     */
    setAuthenticatedUserContext(authenticatedUserId, accountId, storeInCookie = false) {
        try {
            this.appInsights?.setAuthenticatedUserContext(authenticatedUserId, accountId, storeInCookie);
        }
        catch (ex) {
            console.warn('Angular application insights Error [setAuthenticatedUserContext]: ', ex);
        }
    }
    // https://github.com/Microsoft/ApplicationInsights-JS/blob/master/API-reference.md#clearauthenticatedusercontext
    /**
     * Clears the authenticated user id and the account id from the user context.
     */
    clearAuthenticatedUserContext() {
        try {
            this.appInsights?.clearAuthenticatedUserContext();
        }
        catch (ex) {
            console.warn('Angular application insights Error [clearAuthenticatedUserContext]: ', ex);
        }
    }
    _onerror(message) {
        console.warn('Angular application insights Error [_onerror]: ', message);
    }
    /**
     * Initialize Application Insights for Angular
     * Make sure your config{} has been set
     */
    init() {
        if (this.config) {
            // Deprecation Warning(s)
            if (this.config.instrumentationKeySetlater) {
                console.warn(`\n\n
          Warning: [instrumentationKeySetlater] will soon be deprecated.\n
          Use .instrumentationKeySetLater (capital "L" in "L"ater) instead
          to prevent any possible errors in the future!
          \n\n`);
            }
            if (this.config.instrumentationKey) {
                try {
                    this.appInsights = new ApplicationInsights({
                        config: this.config
                    });
                    this.appInsights?.loadAppInsights();
                    // Make sure "router" exists - in case of UIRouterModule where it does not
                    if (!this.config.overrideTrackPageMetrics && this.router) {
                        this.router.events.pipe(filter(event => event instanceof NavigationStart))
                            .subscribe((event) => {
                            this.startTrackPage(event.url);
                        });
                        this.router.events.pipe(filter(event => (event instanceof NavigationEnd ||
                            event instanceof NavigationCancel ||
                            event instanceof NavigationError)))
                            .subscribe((event) => {
                            this.stopTrackPage(event.url);
                        });
                    }
                }
                catch (ex) {
                    console.warn('Angular application insights Error [downloadAndSetup]: ', ex);
                }
            }
            else {
                if (!this.config.instrumentationKeySetLater && !this.config.instrumentationKeySetlater) {
                    // there is no this.config.instrumentationKey AND no this.config.instrumentationKeySetLater => Add log.
                    console.warn('An instrumentationKey value is required to initialize AppInsightsService');
                }
            }
        }
        else {
            console.warn('You need forRoot on ApplicationInsightsModule, with or instrumentationKeySetLater or instrumentationKey set at least');
        }
    }
    get router() {
        try {
            return this._injector.get(Router);
        }
        catch (ex) {
            // @angular/router is not included - App must be utilizing UIRouter
            return null;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: AppInsightsService, deps: [{ token: AppInsightsConfig, optional: true }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: AppInsightsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: AppInsightsService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: AppInsightsConfig, decorators: [{
                    type: Optional
                }] }, { type: i0.Injector }] });

class ArrayHelpersService {
    constructor(i18n) {
        this.i18n = i18n;
    }
    chunk(every, array) {
        let i;
        let j;
        const returnArr = [];
        for (i = 0, j = array.length; i < j; i += every) {
            returnArr.push(array.slice(i, i + every));
        }
        return returnArr;
    }
    unchunk(chunks) {
        return chunks.reduce((acc, chunk) => acc.concat(chunk), []);
    }
    sortByAttributes(input, primaryKey, secondaryKey, reverse = false) {
        const result = input.sort((a, b) => {
            if (a[primaryKey] === b[primaryKey]) {
                if (a[secondaryKey] < b[secondaryKey]) {
                    return -1;
                }
                else if (a[secondaryKey] === b[secondaryKey]) {
                    return 0;
                }
                else {
                    return 1;
                }
            }
            else {
                return (a[primaryKey] < b[primaryKey]) ? -1 : 1;
            }
        });
        return reverse ? result.reverse() : result;
    }
    sort(input, key, reverse = false) {
        return input.sort((left, right) => {
            let leftValue = key ?
                key instanceof Array ? left[key[0]][key[1]] : left[key] :
                left;
            let rightValue = key ?
                key instanceof Array ? right[key[0]][key[1]] : right[key] :
                right;
            if (typeof leftValue === 'string') {
                leftValue = leftValue.toLowerCase();
            }
            if (typeof rightValue === 'string') {
                rightValue = rightValue.toLowerCase();
            }
            if (leftValue < rightValue) {
                return reverse ? 1 : -1;
            }
            if (leftValue > rightValue) {
                return reverse ? -1 : 1;
            }
            return 0;
        });
    }
    mapToUniq(objs, key, subKey) {
        return objs.reduce((acc, obj) => {
            return acc.filter((item) => {
                return obj[key]
                    .some(subItem => subItem[subKey] === item[subKey]);
            });
        }, objs[0] ? objs[0][key] : []);
    }
    arrayToObject(input, key) {
        return input.map((item) => ({
            [key]: item
        }));
    }
    reverse(input) {
        // create a copy of the array (.slice()) then reverse
        return input.slice().reverse();
    }
    /**
     * Checks if all items in the subset are present in the full list
     *
     * @param potentialSubset Potential subset
     * @param fullList Full list
     */
    checkIfArrayIsSubset(potentialSubset, fullList) {
        return fullList.length >= potentialSubset.length &&
            (potentialSubset.every(potentialItem => fullList.includes(potentialItem)));
    }
    /**
     * Adds item if not present, removes item if present
     *
     * @param item Item to be evaluated
     * @param allItems Full list
     */
    toggleItemPresence(item, allItems) {
        let removedExisting = false;
        const newSelectedItems = allItems.filter((existingItem) => {
            if (isEqual(item, existingItem)) {
                removedExisting = true;
                return false;
            }
            return true;
        });
        if (!removedExisting) {
            return [
                ...newSelectedItems,
                item
            ];
        }
        else {
            return newSelectedItems;
        }
    }
    /**
     * Ensures all items are added to an array or, if all items are already in array, removes all items
     *
     * @param toggledItems Items needed to be added or removed
     * @param allItems Array for items to be added from or removed from
     * @returns A list either containing all items, or none of them
     */
    bulkToggleItemPresence(toggledItems, allItems) {
        const allItemsSelected = this.checkIfArrayIsSubset(toggledItems, allItems);
        if (allItemsSelected) {
            const otherItems = allItems.filter(selectedItem => {
                return !toggledItems.includes(selectedItem);
            });
            return [
                ...otherItems
            ];
        }
        else {
            const otherItems = toggledItems.filter(selectedItem => {
                return !allItems.includes(selectedItem);
            });
            return [
                ...allItems,
                ...otherItems
            ];
        }
    }
    /**
     * Should we show previous and next app buttons?
     *
     * @param currentRecordId: Current Record ID
     * @param recordIds: All Record IDs
     * @returns if we should show previous and next buttons
     */
    getPreviousAndNextHelpers(currentRecordId, recordIds) {
        let showPreviousButton = false;
        let showNextButton = false;
        if (!!recordIds) {
            const currentIndex = this.getCurrentIndexOfId(currentRecordId, recordIds);
            if (currentIndex > -1) {
                // Show previous if the current one is not the first
                showPreviousButton = currentIndex !== 0;
                // Show next if there are any records after the current
                showNextButton = currentIndex < (recordIds.length - 1);
            }
        }
        return {
            showPreviousButton,
            showNextButton
        };
    }
    /**
     * Gets the index of the current app ID
     *
     * @param currentRecordId: Current Record ID
     * @param recordIds: All Record IDs
     * @returns the current index
     */
    getCurrentIndexOfId(currentRecordId, recordIds) {
        return (recordIds || []).findIndex((id) => {
            return id === currentRecordId;
        });
    }
    /**
     * Returns the previous app ID in the list
     *
     * @param currentRecordId: Current App ID
     * @param recordIds: All Record IDs
     * @returns the previous app ID in the list
     */
    getPreviousRecordId(currentRecordId, recordIds) {
        const currentIndex = this.getCurrentIndexOfId(currentRecordId, recordIds);
        let previousId = recordIds[currentIndex - 1];
        if (!previousId) {
            // If no previous ID, they are on the 1st record and want to go to the last record
            previousId = recordIds[recordIds.length - 1];
        }
        return previousId;
    }
    /**
     * Returns the next app ID in the list
     *
     * @param currentRecordId: Current App ID
     * @param recordIds: All Record IDs
     * @returns the next app ID in the list
     */
    getNextRecordId(currentRecordId, recordIds) {
        const currentIndex = this.getCurrentIndexOfId(currentRecordId, recordIds);
        let nextRecordId = recordIds[currentIndex + 1];
        if (!nextRecordId) {
            // If no next ID, this means they are at the end and it should go back to the beginning
            nextRecordId = recordIds[0];
        }
        return nextRecordId;
    }
    /**
     * Given an enum mapping, generate a translated description of the enum and what each value represents.
     * For example: Approved = 1, Declined = 2, etc. We use this for explaining the enum values for CSV imports of data that potentially requires the enum in the file
     *
     * @param enumMapping: Map of the enum - Left is enum value and right is i18n key of what it represents
     * @param useBreaks: If the values should be separate by a break or a comma
     * @returns the translated description of that enum
     */
    getEnumMappingForDisplay(enumMapping, useBreaks = true) {
        return Object.keys(enumMapping).reduce((acc, key) => {
            const translation = this.i18n.translate(enumMapping[+key]);
            const mappedText = `${translation} = ${key}`;
            if (!acc) {
                return mappedText;
            }
            else {
                const separator = useBreaks ? '</br>' : ', ';
                return `${acc}${separator}${mappedText}`;
            }
        }, '');
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ArrayHelpersService, deps: [{ token: i1.I18nService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ArrayHelpersService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ArrayHelpersService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.I18nService }] });

/* eslint-disable no-bitwise */
class GuidService {
    constructor() {
        this._generatedUIDs = {};
    }
    /**
     *
     * @returns A random UUID 6 characters long
     */
    short() {
        while (true) {
            const uid = ('000000' + ((Math.random() * Math.pow(36, 6)) | 0).toString(36)).slice(-6);
            if (!this._generatedUIDs.hasOwnProperty(uid)) {
                this._generatedUIDs[uid] = true;
                return uid;
            }
        }
    }
    nonce() {
        const guidHolder = 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxxxxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx';
        const hex = '0123456789abcdef';
        let r = 0;
        let guidResponse = '';
        for (const guidChar of guidHolder) {
            if (guidChar !== '-' && guidChar !== '4') {
                // each x and y needs to be random
                // eslint-disable-next-line no-bitwise
                r = Math.random() * 16 | 0;
            }
            if (guidChar === 'x') {
                guidResponse += hex[r];
            }
            else if (guidChar === 'y') {
                // clock-seq-and-reserved first hex is filtered and remaining hex values are random
                // eslint-disable-next-line no-bitwise
                r &= 0x3; // bit and with 0011 to set pos 2 to zero ?0??
                // eslint-disable-next-line no-bitwise
                r |= 0x8; // set pos 3 to 1 as 1???
                guidResponse += hex[r];
            }
            else {
                guidResponse += guidChar;
            }
        }
        return guidResponse;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: GuidService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: GuidService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: GuidService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class YCTwoWayEmitter {
    registerAction(action) {
        this.action = action;
    }
    emit(arg) {
        return this.action ? this.action(arg) : null;
    }
}

class ToStringPipe {
    transform(value, ...args) {
        return 'toString' in value ? value.toString() : '';
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ToStringPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: ToStringPipe, isStandalone: false, name: "ycToString" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ToStringPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'ycToString',
                    standalone: false
                }]
        }] });

class PluckPipe {
    transform(values, attr) {
        return values.map(value => value[attr]);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: PluckPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: PluckPipe, isStandalone: false, name: "ycPluck" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: PluckPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'ycPluck',
                    standalone: false
                }]
        }] });

class SumPipe {
    transform(values) {
        return values.reduce((sum, value) => sum + value, 0);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: SumPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: SumPipe, isStandalone: false, name: "ycSum" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: SumPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'ycSum',
                    standalone: false
                }]
        }] });

class PercentPipe {
    constructor(moneyService) {
        this.moneyService = moneyService;
    }
    transform(value, decimals = 0) {
        const number = +value;
        return `${this.moneyService.toFixedNumber(number, decimals)}%`;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: PercentPipe, deps: [{ token: i1$1.MoneyService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: PercentPipe, isStandalone: false, name: "ycPercent" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: PercentPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'ycPercent',
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: i1$1.MoneyService }] });

class UtilsModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: UtilsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: UtilsModule, declarations: [ToStringPipe,
            HiddenDirective,
            NullishCoalescePipe,
            PluckPipe,
            SumPipe,
            PercentPipe], exports: [ToStringPipe,
            HiddenDirective,
            NullishCoalescePipe,
            PluckPipe,
            SumPipe,
            PercentPipe] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: UtilsModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: UtilsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ToStringPipe,
                        HiddenDirective,
                        NullishCoalescePipe,
                        PluckPipe,
                        SumPipe,
                        PercentPipe
                    ],
                    exports: [
                        ToStringPipe,
                        HiddenDirective,
                        NullishCoalescePipe,
                        PluckPipe,
                        SumPipe,
                        PercentPipe
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AppInsightsService, ArrayHelpersService, GuidService, HiddenDirective, NullishCoalescePipe, PercentPipe, PluckPipe, SumPipe, ToStringPipe, UtilsModule, YCTwoWayEmitter };
